import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetDescription,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Toaster } from 'sonner';
import { newsAndOffersConfig } from '@/features/news-and-offers/utils/newsAndOffersConfig';
import ClockIconSolid from '@/components/icons/ClockIconSolid';
import ImageIconSolid from '@/components/icons/ImageIconSolid';
import EImageWithFallback from '@/components/everridge/EImageWithFallback';
import { NewsAndOffersType } from '@/features/news-and-offers/types/news-and-offers-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useNewAndOfferDetailsApi } from '@/features/news-and-offers/hooks/useNewAndOfferDetailsApi';
import { ImagesHelper } from '@/utils/ImagesHelper';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';

type Props = {
	id?: string;
	onClose: () => void;
};

const NewAndOfferDetails: FC<Props> = ({ id, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const { newAndOfferDetailsData } = useNewAndOfferDetailsApi({ id });

	if (!newAndOfferDetailsData) return null;

	const {
		label,
		icon: Icon,
		className,
	} = newsAndOffersConfig.getCardIcon(newAndOfferDetailsData.type);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	return (
		<Sheet
			open={isOpen}
			onOpenChange={handleClose}>
			<SheetContent
				className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
				<SheetHeader>
					<SheetTitle>
						{!isMobile && (
							<SheetClose
								className={cn(
									'flex items-center justify-center cursor-pointer',
									isMobile &&
										'size-11 border border-popover-100 rounded-full',
								)}>
								<MoveLeftIcon />
							</SheetClose>
						)}
						{t('main.newsAndOffers.components.details.title')}
						{isMobile && (
							<SheetClose className='size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full'>
								<Cancel_01_Icon />
							</SheetClose>
						)}
					</SheetTitle>
					<SheetDescription />
				</SheetHeader>
				<div className='flex-1 flex flex-col bg-popover-30 overflow-hidden select-none'>
					<div
						className={cn(
							'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
							isMobile && 'p-4 gap-4',
						)}>
						<div className='flex flex-col gap-6'>
							<div className='flex justify-between text-sm'>
								<div className='flex items-center gap-1.5'>
									<Icon className={className} />
									{t(label)}
								</div>
								<div className='flex items-center gap-1.5'>
									<ClockIconSolid className='fill-popover-800' />
									{DateTimeHelper.formatDate(
										newAndOfferDetailsData.startDate,
										2,
									)}
								</div>
							</div>
							<div className='h-94.5 rounded-semi-xl overflow-hidden'>
								<EImageWithFallback
									src={ImagesHelper.getImageUrl(
										newAndOfferDetailsData.imageUrl,
									)}
									fallbackSVG={ImageIconSolid}
								/>
							</div>
							<div className='flex flex-col gap-1.5 text-sm text-popover-800'>
								<h3
									className={cn(
										'text-lg font-medium',
										isMobile && 'text-base',
									)}>
									{newAndOfferDetailsData.title}
								</h3>
								<p className='mb-3 text-sm text-popover-800'>
									{newAndOfferDetailsData.description}
								</p>
							</div>
						</div>
					</div>
				</div>
				<Toaster />
			</SheetContent>
		</Sheet>
	);
};

export default NewAndOfferDetails;
